/**
 * Key that changes whenever the What's new widget should be rebuilt, so it
 * downloads the updates again.
 *
 * Featurebase's changelog widget only fetches updates when it starts, so an
 * update published while the app is open never reaches the unread count.
 * Remounting the widget (its hook tears down and re-inits on remount) makes
 * it fetch again. We do that on a timer and whenever the engineer comes back
 * to the tab, but never while the tab is hidden or the updates popup is open
 * (a rebuild would close it under them).
 */
import { useEffect, useState } from 'react'

// Updates are rare, and coming back to the tab also refreshes, so a slow
// timer is enough and keeps requests to Featurebase low.
const CHANGELOG_REFRESH_INTERVAL_MS = 5 * 60_000

// Featurebase's updates popup while it's showing. Only used to skip a
// rebuild; if Featurebase renames it, the worst case is a popup closing.
const OPEN_CHANGELOG_POPUP_SELECTOR =
  '.fb-changelog-popup-overlay:not(.fb-changelog-popup-overlay-hidden)'

function isChangelogPopupOpen(): boolean {
  return document.querySelector(OPEN_CHANGELOG_POPUP_SELECTOR) !== null
}

function canRefreshNow(): boolean {
  return document.visibilityState === 'visible' && !isChangelogPopupOpen()
}

export function useChangelogRefreshKey(): number {
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    const refresh = () => {
      if (canRefreshNow()) {
        setRefreshKey((key) => key + 1)
      }
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh()
      }
    }

    const timer = window.setInterval(refresh, CHANGELOG_REFRESH_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return refreshKey
}
